import config from "../config";
import {getData, getProperty} from "./http-request-utils";
import {httpDelete, httpGet, httpPatch, httpPost} from "./http-functions";
import {
    AddGroupGuest,
    AddGroupProvider,
    Folder,
    FolderWithChildren,
    Group,
    Guest,
    ProGuestUser,
    Provider,
    SearchGroup,
    SearchGroupGuests,
    SearchGroupProviders,
    SearchProviderGuests,
    SearchProviders,
    UpsertGroupRequest
} from "../models/pro-guest.models";
import {PaginatedSearchResult, UserToAdd} from "../models/shared";

export const addProviderGuest = async (organizationId: number, guest: UserToAdd) => {
    const resp = await httpPost(`${config.api}/providers/organizations/${organizationId}`, guest);
    return getData(resp);
};


export const getProvider = async (providerId: number) => {
    const resp = await httpGet(`${config.api}/providers/${providerId}`);
    return getData(resp);
};


export const removeProviderGuest = async (organizationId: number, guestId: number): Promise<number> => {
    const resp = await httpDelete(`${config.api}/providers/organizations/${organizationId}/my-guests/${guestId}`);
    return getData(resp);
};

export const resendInviteProviderGuest = async (organizationId: number, guestId: number) => {
    const resp = await httpPost(`${config.api}/providers/organizations/${organizationId}/my-guests/${guestId}/resend`);
    return getData(resp);
};

export const getProviderGuests = async (providerUserId: string, ownGuestsOnly = false) => {
    const url = `${config.api}/providers/${providerUserId}/guests?ownGuestsOnly=${ownGuestsOnly}`;
    const resp = await httpGet(url);
    return getData(resp)?.guests;
};

export const getAllMyGuests = async (organizationId: number) => {
    const url = `${config.api}/providers/organizations/${organizationId}/my-guests`;
    const resp = await httpGet(url);
    return getData(resp);
};

export const searchProviderGuests = async ({
                                               organizationId,
                                               size,
                                               page,
                                               filter
                                           }: SearchProviderGuests): Promise<PaginatedSearchResult<ProGuestUser>> => {
    const url = `${config.api}/providers/organizations/${organizationId}/my-guests/search`;
    const resp = await httpPost(url, {page, size, filter});
    return getData(resp);
};

export const addExternalProvider = async (organizationId: number, folderId: number, externalProvider: UserToAdd) => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/external-providers`;
    const resp = await httpPost(url, externalProvider);
    return resp.data;
};

export const searchProviders = async ({
                                          organizationId,
                                          folderId,
                                          size,
                                          page,
                                          filter,
                                          groupId
                                      }: SearchProviders): Promise<PaginatedSearchResult<Provider>> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/providers/search`;
    const result = await httpPost(url, {page, size, filter, groupId});

    return ({
        page,
        size,
        total: getProperty('total')(result),
        items: getProperty('items')(result)
    });
};

export const searchGroups = async ({
                                       organizationId,
                                       folderId,
                                       size,
                                       page,
                                       filter
                                   }: SearchGroup): Promise<PaginatedSearchResult<Group>> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/search`;
    const result = await httpPost(url, {page, size, filter});

    return ({
        page,
        size,
        total: getProperty('total')(result),
        items: getProperty('items')(result)
    });
};


export const getTopFolders = async (organizationId: number): Promise<Folder[]> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/tops`;
    const resp = await httpGet(url);
    return resp.data;
};

export const getFolderAncestors = async ({
                                             organizationId,
                                             folderId,
                                             recursive = false
                                         }: { organizationId: number, folderId: number, recursive: boolean }): Promise<Folder[]> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/ancestors`;
    const result = await httpGet(url, {params: {recursive}});
    return getData(result);
};

export const getFolderChildren = async (organizationId: number, folderId: number): Promise<Folder[]> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/children`;
    const resp = await httpGet(url);
    return resp.data;
};

export const getFolder = async (organizationId: number, folderId: number): Promise<Folder> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}`;
    const resp = await httpGet(url);
    return resp.data;
};

export const getTopFoldersWithChildren = async (organizationId: number, selectedFolderId: number, topFolderIds: Set<number>): Promise<FolderWithChildren[]> => {
    const folders = (await getTopFolders(organizationId))
        .map((f: Folder) => ({
            ...f,
            isTop: true
        }));

    if (!(folders && folders.length)) {
        return [];
    }

    const selectedFolder = folders
            .find((f: Folder) => f.id === selectedFolderId)
        || folders[0];

    const parentFolderId = getParentFolderId(selectedFolder, topFolderIds);

    const children = await getFolderChildren(organizationId, selectedFolder.id);

    return folders.map((f: Folder) => f.id !== selectedFolder.id
        ? f
        : {
            ...f,
            children,
            isSelected: true,
            parentFolderId
        });
};

export const getFoldersWithChildren = async (organizationId: number, folderId: number, topFolderIds: Set<number>): Promise<FolderWithChildren[]> => {
    const folder = await getFolder(organizationId, folderId);
    const children = await getFolderChildren(organizationId, folder.id);
    const parentFolderId = getParentFolderId(folder, topFolderIds);
    return [
        {
            ...folder,
            children,
            isSelected: true,
            parentFolderId
        }
    ];
};

const getParentFolderId = (folder: Folder, topFolderIds: Set<number>): number | undefined => {
    return folder.parentFolderId && (topFolderIds && !topFolderIds.has(folder.id))
        ? folder.parentFolderId
        : undefined;
};

export const getGroup = async ({
                                   organizationId,
                                   folderId,
                                   groupId
                               }: { organizationId: number, folderId: number, groupId: number }): Promise<Group> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${groupId}`;
    const result = await httpGet(url);
    return getData(result);
};

export const addGroup = async ({
                                   organizationId,
                                   folderId,
                                   group
                               }: { organizationId: number, folderId: number, group: { name: string } }): Promise<Group> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups`;
    const result = await httpPost(url, group);
    return getData(result);
};

export const updateGroup = async ({
                                      organizationId,
                                      folderId,
                                      group
                                  }: UpsertGroupRequest): Promise<Group> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${group.id}`;
    const result = await httpPatch(url, group);
    return getData(result);
};

export const upsertGroup = async ({
                                      group,
                                      ...args
                                  }: UpsertGroupRequest): Promise<Group> => {
    return group.id ? updateGroup({group, ...args}) : addGroup({group, ...args});
};

export const searchGroupProviders = async ({
                                               organizationId,
                                               folderId,
                                               groupId,
                                               size,
                                               page,
                                               filter
                                           }: SearchGroupProviders): Promise<PaginatedSearchResult<Provider>> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${groupId}/providers/search`;
    const result = await httpPost(url, {page, size, filter});

    return ({
        page,
        size,
        total: getProperty('total')(result),
        items: getProperty('items')(result)
    });
};

export const searchGroupGuests = async ({
                                            organizationId,
                                            folderId,
                                            groupId,
                                            size,
                                            page,
                                            filter,
                                            onlyPending
                                        }: SearchGroupGuests): Promise<PaginatedSearchResult<Guest>> => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${groupId}/guests/search`;
    const result = await httpPost(url, {page, size, filter, onlyPending});

    return ({
        page,
        size,
        total: getProperty('total')(result),
        items: getProperty('items')(result)
    });
};

export const addGroupProvider = async ({
                                           organizationId,
                                           folderId,
                                           groupId,
                                           providerId
                                       }: AddGroupProvider) => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${groupId}/providers`;
    return await httpPost(url, {providerId, groupId});
};

export const addGroupGuest = async ({
                                        organizationId,
                                        folderId,
                                        groupId,
                                        guest
                                    }: AddGroupGuest) => {
    const url = `${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${groupId}/guests`;
    return await httpPost(url, {groupId, guest});
};

export const removeProGroupGuest = async (organizationId: number, folderId: number, groupId: number, guestId: number): Promise<number> => {
    const resp = await httpDelete(`${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${groupId}/guests/${guestId}`);
    return getData(resp);
};

export const resendInviteGroupGuest = async (organizationId: number, folderId: number, groupId: number, guestId: number) => {
    const resp = await httpPost(`${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${groupId}/guests/${guestId}/resend`);
    return getData(resp);
};

export const removeProGroupProvider = async (organizationId: number, folderId: number, groupId: number, providerId: number): Promise<number> => {
    const resp = await httpDelete(`${config.api}/providers/organizations/${organizationId}/folders/${folderId}/groups/${groupId}/providers/${providerId}`);
    return getData(resp);
};

export const getGuestStatus = (guest: Guest) => guest.isEnabled === undefined ? `Pending` :
    (guest.isEnabled === true ? `Accepted` : `Declined`);
